import {useCallback} from 'react';
import {Platform} from 'react-native';
import {useFocusEffect} from '@react-navigation/native';

import {useOptionalScrollCollapseContext} from '../context/ScrollCollapseContext';

type Options = {
  /** Skip resetting scroll-collapse state on focus. Set true when the screen
   *  already uses useScrollCollapseHandler (which has its own focus sync). */
  skipFocusReset?: boolean;
};

/**
 * Returns the bottom padding needed to compensate for an absolutely-positioned tab bar. Always
 * 0: native lays the tab bar out in flow, and web has no tab bar since QA 2026-10-21 (the
 * header and footer carry its links; TabNavigator passes an empty tabBar on web). It returned
 * TAB_BAR_HEIGHT + inset on web mobile while the AnimatedTabBar overlaid content there.
 *
 * Also resets scroll-collapse state on focus so that the header and tab bar
 * start visible when navigating to a screen that doesn't have its own
 * scroll-collapse handler (e.g. Product Detail, Login, Checkout screens).
 * Pass `{skipFocusReset: true}` for screens that already use useScrollCollapseHandler.
 */
export function useTabBarBottomPadding(options?: Options): number {
  const ctx = useOptionalScrollCollapseContext();
  const skipReset = options?.skipFocusReset ?? false;

  // Reset scroll-collapse state when this screen gains focus.
  // Screens with useScrollCollapseHandler have their own useScrollFocusSync;
  // this covers screens that don't participate in scroll-collapse but still
  // need the header/tab bar visible.
  useFocusEffect(
    useCallback(() => {
      if (skipReset || !ctx || Platform.OS !== 'web') return;
      ctx.scrollY.set(0);
      ctx.prevScrollY.set(0);
      ctx.accumulatedUp.set(ctx.TAB_BAR_HEIGHT);
      ctx.accumulatedDown.set(0);
    }, [ctx, skipReset]),
  );

  return 0;
}
